/**
 * Automation Ran Event Component
 *
 * Renders ahoy_automation_ran run records — one automation run's outcome,
 * shared by event-triggered runs, clock fires, manual run-now, and fire-time
 * guard skips.
 *
 * Run records only ever sit on the automation's own record, so the row does
 * not name an actor (it would always be the automation itself). The header
 * leads with the outcome and how the run arrived. Everything beneath is the
 * timeline's own sub-item list — the same indented icon-and-connector list a
 * bundled property update or a formula error uses — so a run reads like any
 * other event on the record rather than a foreign panel:
 *
 * - a failure or skip opens with one sentence derived from the structured
 *   `category` slug;
 * - each line of the script's log (`payload.summary`, org-scoped markdown so
 *   entity tags and alert blocks resolve) is a sub-item, collapsed past a
 *   handful. Skips carry no log worth showing: the scheduler's skip summary
 *   is just the reason slug, already said in prose.
 * - a failed run additionally renders the "Ask Ahoy what went wrong"
 *   investigation CTA beneath the run's content, seeded with the specific
 *   (never generic) reason sentence.
 */
import { AlertTriangle, SlashCircle01, Terminal } from '@untitledui/icons';
import type { ReactNode } from 'react';
import { useId, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { BadgeWithDot } from '@/lib/base/badges/badges';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';

import { MARKDOWN_ALERT_CLASSNAME, TimelineEvent } from '../components/shared';
import type { AutomationRanPayload } from '../types';
import type { SubItemContent } from '../types/renderer';
import { AutomationFailureChatButton } from './AutomationFailureChatButton';
import type { EventComponentProps } from './types';

type RunStatus = NonNullable<AutomationRanPayload['status']>;

const STATUS_LABELS: Record<RunStatus, string> = {
  success: 'Succeeded',
  error: 'Failed',
  skipped: 'Skipped',
};

const STATUS_COLORS: Record<RunStatus, 'success' | 'error' | 'gray'> = {
  success: 'success',
  error: 'error',
  skipped: 'gray',
};

const PROVENANCE_LABELS: Record<
  NonNullable<AutomationRanPayload['provenance']>,
  string
> = {
  manual: 'Manual run',
  clock: 'Scheduled run',
  event: 'Triggered by an event',
};

/**
 * One sentence per machine-readable reason slug (apps/automations):
 * skip reasons from the timetable handler's fire-time guards, failure
 * categories from the execution pipeline. Unknown slugs fall back to the
 * sanctioned generic line rather than leaking the slug.
 */
const REASON_SENTENCES: Record<string, string> = {
  // Fire-time guard skips
  disabled: 'This automation is off, so the scheduled run was skipped.',
  no_schedule: 'This automation has no schedule, so the run was skipped.',
  entity_deleted: 'This automation was deleted before the run started.',
  malformed_schedule: "We couldn't read this automation's schedule.",
  // Execution failures
  no_usable_script: 'This automation has no script to run.',
  rpc_call_budget_exceeded:
    'This run needed to look up more records than a single run can, so it stopped without saving any changes. Running it again will hit the same limit.',
  output_validation_rejected:
    "The changes this run produced didn't pass validation, so none were saved.",
  output_events_cap_exceeded:
    'This run produced more changes than we allow at once, so none were saved.',
  emission_output_dropped: "Some of this run's output couldn't be delivered.",
  emission_handler_unavailable:
    "We couldn't reach a service this run needed. It'll be retried on the next trigger.",
  output_event_malformed:
    "The script's output wasn't in a shape we could apply.",
  output_event_not_a_mapping:
    "The script's output wasn't in a shape we could apply.",
  output_event_unknown_type:
    "The script's output wasn't in a shape we could apply.",
  stdout_not_a_mapping: "The script's output wasn't in a shape we could apply.",
  stdout_decode_error: "The script's output wasn't in a shape we could apply.",
  bundle_defects: "The script's output wasn't in a shape we could apply.",
  audit_bytes_cap_exceeded: "This run's log was too large to save.",
  run_record_write_failed: "We couldn't record this run.",
};

const GENERIC_FAILURE =
  'Something went wrong. Try again, and get in touch if it keeps happening.';
const GENERIC_SKIP = 'This run was skipped.';

// The backend joins one message per line with a markdown hard break
// ("  \n"); an alert block ("> [!CAUTION]\n> …") stays one message.
const LOG_LINE_SEPARATOR = /\s{2,}\n/;
const LOG_COLLAPSE_AFTER = 5;

function logMoreLabel(hiddenCount: number): string {
  return `+${hiddenCount} more ${hiddenCount === 1 ? 'line' : 'lines'}`;
}

/**
 * The reason sentence for a specific, known failure category only — never
 * the generic fallback. Used for the investigation CTA's prompt, so a
 * "Something went wrong…" line is never put in the user's prompt.
 *
 * Checks own-key membership before indexing so an inherited name such as
 * `toString` can never resolve to a function (which would leak function
 * source into the prompt or the rendered reason).
 */
function specificReasonSentence(category: string | undefined): string | null {
  return category &&
    Object.prototype.hasOwnProperty.call(REASON_SENTENCES, category)
    ? REASON_SENTENCES[category]
    : null;
}

function reasonSentence(
  status: RunStatus,
  category: string | undefined
): string | undefined {
  if (status === 'success') {
    return undefined;
  }
  const specific = specificReasonSentence(category);
  if (specific) {
    return specific;
  }
  return status === 'skipped' ? GENERIC_SKIP : GENERIC_FAILURE;
}

function isAlertBlock(line: string): boolean {
  return line.startsWith('> [!');
}

function reasonSubItem(status: RunStatus, reason: string): SubItemContent {
  if (status === 'error') {
    return {
      icon: <AlertTriangle className='w-4 h-4 text-utility-error-500' />,
      text: <span className='text-utility-error-700'>{reason}</span>,
      ariaLabel: reason,
    };
  }
  return {
    icon: <SlashCircle01 className='w-4 h-4 text-tertiary' />,
    text: <span className='text-tertiary'>{reason}</span>,
    ariaLabel: reason,
  };
}

function logSubItem(line: string, orgId: number): SubItemContent {
  const alert = isAlertBlock(line);
  const text: ReactNode = (
    <MarkdownContent
      content={line}
      orgId={orgId}
      size='compact'
      as={alert ? 'div' : 'span'}
      className={alert ? MARKDOWN_ALERT_CLASSNAME : undefined}
    />
  );
  return {
    icon: alert ? (
      <AlertTriangle className='w-4 h-4 text-utility-error-500' />
    ) : (
      <Terminal className='w-4 h-4 text-tertiary' />
    ),
    text,
    ariaLabel: line,
  };
}

export function AutomationRanEvent({ event }: EventComponentProps) {
  const { orgId } = useParams<{ orgId: string }>();
  const orgIdNum = orgId ? Number(orgId) : undefined;
  const validOrgId =
    orgIdNum !== undefined && Number.isFinite(orgIdNum) ? orgIdNum : undefined;
  const headerId = useId();

  const payload = event.payload as AutomationRanPayload | undefined;
  const status: RunStatus = payload?.status ?? 'success';
  const statusLabel = STATUS_LABELS[status];
  const provenanceLabel = payload?.provenance
    ? PROVENANCE_LABELS[payload.provenance]
    : undefined;
  const reason = reasonSentence(status, payload?.category);
  const rawSummary = payload?.summary;

  const subItems = useMemo<SubItemContent[]>(() => {
    const items: SubItemContent[] = [];
    if (reason) {
      items.push(reasonSubItem(status, reason));
    }
    if (
      status !== 'skipped' &&
      validOrgId !== undefined &&
      typeof rawSummary === 'string'
    ) {
      for (const line of rawSummary.split(LOG_LINE_SEPARATOR)) {
        const trimmed = line.trim();
        if (trimmed) {
          items.push(logSubItem(trimmed, validOrgId));
        }
      }
    }
    return items;
  }, [reason, status, rawSummary, validOrgId]);

  const ariaLabel = provenanceLabel
    ? `${statusLabel} – ${provenanceLabel}`
    : statusLabel;

  return (
    <TimelineEvent ariaLabel={ariaLabel}>
      <TimelineEvent.Icon event={event} />
      <TimelineEvent.Content
        eventId={event.event_id}
        header={
          <div id={headerId} className='flex flex-wrap items-center gap-2'>
            <BadgeWithDot size='sm' color={STATUS_COLORS[status]}>
              {statusLabel}
            </BadgeWithDot>
            {provenanceLabel && (
              <span className='text-body-small text-fg-secondary'>
                {provenanceLabel}
              </span>
            )}
          </div>
        }
        timestamp={event.occurred_at}
        subItems={subItems}
        subItemsCollapseAfter={LOG_COLLAPSE_AFTER}
        subItemsMoreLabel={logMoreLabel}
      >
        {status === 'error' && validOrgId !== undefined && event.entity_id && (
          <div className='mt-2 flex flex-wrap'>
            <AutomationFailureChatButton
              orgId={validOrgId}
              automationEntityId={event.entity_id}
              automationEntityType={event.entity_type_name}
              provenance={payload?.provenance}
              occurredAt={event.occurred_at}
              reason={specificReasonSentence(payload?.category)}
              category={payload?.category}
              describedById={headerId}
            />
          </div>
        )}
      </TimelineEvent.Content>
    </TimelineEvent>
  );
}
